import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAudit } from '../hooks/useAudit';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval,
  isSameMonth, isSameDay, isToday, differenceInDays, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function badgeAlerta(diasRestantes) {
  if (diasRestantes < 0) return { label: `${Math.abs(diasRestantes)}d atrasado`, cls: 'badge-danger' };
  if (diasRestantes === 0) return { label: 'Hoje', cls: 'badge-teal' };
  if (diasRestantes <= 7) return { label: `${diasRestantes}d`, cls: 'badge-orange' };
  if (diasRestantes <= 15) return { label: `${diasRestantes}d`, cls: 'badge-blue' };
  return { label: `${diasRestantes}d`, cls: 'badge-gray' };
}

function ModalRemarcar({ retorno, onClose, onSaved }) {
  const { auditEditou } = useAudit();
  const [novaData, setNovaData] = useState('');
  const [obs, setObs] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSalvar() {
    if (!novaData) return;
    setSaving(true);
    await supabase.from('agenda_retornos').update({
      data_prevista: novaData,
      data_prevista_original: retorno.data_prevista_original || retorno.data_prevista,
      remarcada: true,
      observacao: obs || retorno.observacao,
    }).eq('id', retorno.id);
    auditEditou('agenda_retornos', retorno.id, { acao: 'remarcou', data_anterior: retorno.data_prevista, nova_data: novaData });
    setSaving(false);
    onSaved();
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h3>Remarcar retorno</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="alert alert-info" style={{ marginBottom: 16 }}>
            Data atual: <strong>{format(parseISO(retorno.data_prevista), 'dd/MM/yyyy')}</strong>
            {retorno.remarcada && <span className="text-muted" style={{ marginLeft: 8 }}>(já remarcado)</span>}
          </div>
          <div className="form-group">
            <label className="form-label">Nova data *</label>
            <input className="form-input" type="date" value={novaData} onChange={e => setNovaData(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Observação</label>
            <textarea className="form-textarea" value={obs} onChange={e => setObs(e.target.value)} placeholder="Motivo da remarcação..." style={{ minHeight: 60 }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSalvar} disabled={saving || !novaData}>
            {saving ? 'Salvando...' : 'Confirmar remarcação'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CalendarioPage() {
  const { auditVisualizou, auditEditou } = useAudit();
  const [retornos, setRetornos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mesAtual, setMesAtual] = useState(new Date());
  const [diaSel, setDiaSel] = useState(null);
  const [filtro, setFiltro] = useState('pendentes');
  const [remarcar, setRemarcar] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('agenda_retornos')
      .select(`
        id, data_prevista, data_prevista_original, data_realizada,
        observacao, remarcada, alerta_15_enviado, alerta_7_enviado,
        pesquisa_paciente_id(
          paciente_id(iniciais, prontuario),
          pesquisa_id(titulo)
        ),
        momento_id(label),
        criado_por(nome_completo)
      `)
      .order('data_prevista');
    setRetornos(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    auditVisualizou('agenda_retornos', null);
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  async function marcarRealizado(id) {
    await supabase.from('agenda_retornos').update({ data_realizada: new Date().toISOString().split('T')[0] }).eq('id', id);
    auditEditou('agenda_retornos', id, { acao: 'marcou_realizado' });
    load();
  }

  const hoje = new Date();
  const diasDoMes = eachDayOfInterval({ start: startOfMonth(mesAtual), end: endOfMonth(mesAtual) });
  const primeiroDia = startOfMonth(mesAtual).getDay();

  const retornosFiltrados = retornos.filter(r => {
    if (filtro === 'pendentes') return !r.data_realizada;
    if (filtro === 'realizados') return !!r.data_realizada;
    if (filtro === 'proximos') {
      const dias = differenceInDays(parseISO(r.data_prevista), hoje);
      return !r.data_realizada && dias >= 0 && dias <= 30;
    }
    if (filtro === 'atrasados') {
      return !r.data_realizada && differenceInDays(parseISO(r.data_prevista), hoje) < 0;
    }
    return true;
  });

  const retornosDoMes = retornos.filter(r => {
    const d = parseISO(r.data_prevista);
    return isSameMonth(d, mesAtual) && !r.data_realizada;
  });

  const retornosDoDia = diaSel
    ? retornos.filter(r => isSameDay(parseISO(r.data_prevista), diaSel) && !r.data_realizada)
    : [];

  const alertas = retornos.filter(r => {
    if (r.data_realizada) return false;
    const dias = differenceInDays(parseISO(r.data_prevista), hoje);
    return dias >= 0 && dias <= 15;
  });

  return (
    <div>
      <style>{`
        .cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; }
        .cal-header-day { text-align: center; font-size: 11px; font-weight: 600; color: var(--text-3); padding: 6px 0; text-transform: uppercase; letter-spacing: .06em; }
        .cal-day { min-height: 68px; padding: 6px; border-radius: 8px; border: 1px solid transparent; cursor: pointer; transition: all .15s; position: relative; background: var(--surface); }
        .cal-day:hover { border-color: var(--blue); background: #eff6ff; }
        .cal-day.outro-mes { opacity: .3; pointer-events: none; }
        .cal-day.hoje { border-color: var(--teal); background: #f0fdfa; }
        .cal-day.selecionado { border-color: var(--blue); background: #eff6ff; box-shadow: 0 0 0 2px rgba(30,79,194,.2); }
        .cal-day-num { font-size: 13px; font-weight: 600; color: var(--text-2); margin-bottom: 4px; }
        .cal-day.hoje .cal-day-num { color: var(--teal); }
        .cal-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--blue); display: inline-block; margin: 1px; }
        .cal-dot.urgente { background: var(--danger); }
        .cal-dot.breve { background: var(--warning); }
        .retorno-card { padding: 14px 16px; border: 1px solid var(--border); border-radius: 10px; background: var(--surface); margin-bottom: 10px; }
        .retorno-card:hover { border-color: var(--blue); }
        .badge-danger { background: #fee2e2; color: var(--danger); }
        @media (max-width: 768px) {
          .cal-layout { grid-template-columns: 1fr !important; }
          .cal-day { min-height: 44px !important; padding: 4px !important; }
          .cal-day-num { font-size: 12px !important; }
          .cal-header-day { font-size: 10px !important; padding: 4px 0 !important; }
        }
      `}</style>

      <div className="page-header">
        <div>
          <h1>Calendário de Retornos</h1>
          <p>Acompanhe e gerencie as consultas de seguimento</p>
        </div>
      </div>

      {alertas.length > 0 && (
        <div className="card" style={{ marginBottom: 20, borderColor: '#fbbf24', background: '#fffbeb' }}>
          <div className="card-body" style={{ padding: '14px 20px' }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#92400e', marginBottom: 10 }}>
              ⚠️ {alertas.length} retorno{alertas.length > 1 ? 's' : ''} nos próximos 15 dias
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {alertas.map(r => {
                const dias = differenceInDays(parseISO(r.data_prevista), hoje);
                const { label, cls } = badgeAlerta(dias);
                return (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: '#fff', borderRadius: 8, border: '1px solid #fde68a', fontSize: 13 }}>
                    <strong>{r.pesquisa_paciente_id?.paciente_id?.iniciais}</strong>
                    <span className="text-muted">·</span>
                    <span>{r.momento_id?.label || 'Retorno'}</span>
                    <span className={`badge ${cls}`}>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 20 }} className="cal-layout">
        {/* CALENDÁRIO */}
        <div className="card">
          <div className="card-header">
            <div className="flex-row">
              <button className="btn btn-sm btn-secondary" onClick={() => setMesAtual(m => new Date(m.getFullYear(), m.getMonth() - 1))}>‹</button>
              <h2 style={{ margin: '0 8px', textTransform: 'capitalize' }}>
                {format(mesAtual, "MMMM 'de' yyyy", { locale: ptBR })}
              </h2>
              <button className="btn btn-sm btn-secondary" onClick={() => setMesAtual(m => new Date(m.getFullYear(), m.getMonth() + 1))}>›</button>
            </div>
            <button className="btn btn-sm btn-secondary" onClick={() => setMesAtual(new Date())}>Hoje</button>
          </div>
          <div className="card-body">
            <div className="cal-grid" style={{ marginBottom: 8 }}>
              {DIAS_SEMANA.map(d => <div key={d} className="cal-header-day">{d}</div>)}
            </div>
            <div className="cal-grid">
              {Array.from({ length: primeiroDia }).map((_, i) => <div key={`empty-${i}`} />)}
              {diasDoMes.map(dia => {
                const retornosDia = retornosDoMes.filter(r => isSameDay(parseISO(r.data_prevista), dia));
                const temUrgente = retornosDia.some(r => differenceInDays(parseISO(r.data_prevista), hoje) <= 7);
                const temBreve = retornosDia.some(r => {
                  const d = differenceInDays(parseISO(r.data_prevista), hoje);
                  return d > 7 && d <= 15;
                });
                return (
                  <div
                    key={dia.toISOString()}
                    className={`cal-day ${isToday(dia) ? 'hoje' : ''} ${diaSel && isSameDay(dia, diaSel) ? 'selecionado' : ''}`}
                    onClick={() => setDiaSel(diaSel && isSameDay(dia, diaSel) ? null : dia)}
                  >
                    <div className="cal-day-num">{format(dia, 'd')}</div>
                    <div>
                      {retornosDia.slice(0, 3).map((r, i) => (
                        <div key={i} className={`cal-dot ${temUrgente ? 'urgente' : temBreve ? 'breve' : ''}`} style={{ display: 'inline-block' }} />
                      ))}
                      {retornosDia.length > 3 && <span style={{ fontSize: 10, color: 'var(--text-3)' }}>+{retornosDia.length - 3}</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            {diaSel && (
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>
                  {format(diaSel, "EEEE, d 'de' MMMM", { locale: ptBR })}
                  {retornosDoDia.length === 0 && <span className="text-muted" style={{ marginLeft: 8, fontWeight: 400 }}>— nenhum retorno</span>}
                </div>
                {retornosDoDia.map(r => (
                  <div key={r.id} className="retorno-card">
                    <div className="flex-between">
                      <div>
                        <strong>{r.pesquisa_paciente_id?.paciente_id?.iniciais}</strong>
                        <span className="mono text-muted text-sm" style={{ marginLeft: 8 }}>{r.pesquisa_paciente_id?.paciente_id?.prontuario}</span>
                      </div>
                      <div className="flex-row" style={{ gap: 6 }}>
                        <button className="btn btn-sm btn-secondary" onClick={() => setRemarcar(r)}>Remarcar</button>
                        <button className="btn btn-sm btn-primary" onClick={() => marcarRealizado(r.id)}>✓ Realizado</button>
                      </div>
                    </div>
                    <div className="text-sm text-muted" style={{ marginTop: 4 }}>
                      {r.pesquisa_paciente_id?.pesquisa_id?.titulo} · {r.momento_id?.label}
                      {r.remarcada && <span className="badge badge-orange" style={{ marginLeft: 8 }}>Remarcado</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* LISTA LATERAL */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="card">
            <div className="card-body" style={{ padding: '12px 16px' }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[['pendentes', 'Pendentes'], ['proximos', 'Próximos 30d'], ['atrasados', 'Atrasados'], ['realizados', 'Realizados']].map(([val, label]) => (
                  <button key={val} className={`btn btn-sm ${filtro === val ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFiltro(val)}>{label}</button>
                ))}
              </div>
            </div>
          </div>

          <div className="card" style={{ flex: 1, overflow: 'hidden' }}>
            <div className="card-header">
              <h2>Lista ({retornosFiltrados.length})</h2>
            </div>
            <div style={{ maxHeight: 520, overflowY: 'auto', padding: '12px 16px' }}>
              {loading ? (
                <div className="text-muted text-sm">Carregando...</div>
              ) : retornosFiltrados.length === 0 ? (
                <div className="empty-state" style={{ padding: 24 }}>
                  <div className="empty-icon">📅</div>
                  <h3>Nenhum retorno</h3>
                </div>
              ) : retornosFiltrados.map(r => {
                const dias = differenceInDays(parseISO(r.data_prevista), hoje);
                const { label: labelDias, cls } = badgeAlerta(dias);
                return (
                  <div key={r.id} className="retorno-card" style={{ cursor: 'default' }}>
                    <div className="flex-between" style={{ marginBottom: 4 }}>
                      <strong style={{ fontSize: 14 }}>{r.pesquisa_paciente_id?.paciente_id?.iniciais}</strong>
                      {!r.data_realizada && <span className={`badge ${cls}`}>{labelDias}</span>}
                      {r.data_realizada && <span className="badge badge-teal">✓ Realizado</span>}
                    </div>
                    <div className="text-sm text-muted">{r.momento_id?.label}</div>
                    <div className="text-sm" style={{ marginTop: 4, fontWeight: 500 }}>
                      {format(parseISO(r.data_prevista), 'dd/MM/yyyy')}
                      {r.remarcada && <span className="badge badge-orange" style={{ marginLeft: 6, fontSize: 10 }}>remarcado</span>}
                    </div>
                    {!r.data_realizada && (
                      <div className="flex-row" style={{ gap: 6, marginTop: 8 }}>
                        <button className="btn btn-sm btn-secondary" style={{ flex: 1 }} onClick={() => setRemarcar(r)}>Remarcar</button>
                        <button className="btn btn-sm btn-primary" style={{ flex: 1 }} onClick={() => marcarRealizado(r.id)}>✓ Realizado</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {remarcar && (
        <ModalRemarcar
          retorno={remarcar}
          onClose={() => setRemarcar(null)}
          onSaved={() => { setRemarcar(null); load(); }}
        />
      )}
    </div>
  );
}
